import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExchangeAlt,
  faExclamationTriangle,
  faHistory,
  faInfoCircle,
  faPills,
  faSlidersH,
  faTruck
} from "@fortawesome/free-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { toast } from "react-toastify";
import moment from "moment";
import axios from "helpers/api";
import ScheduleBadge from "./ScheduleBadge";

/**
 * Stock history for one of a patient's medications: every delivery received,
 * dose given and adjustment, newest first, with the balance after each.
 * Above it, the current balance and an inventory adjustment: staff enter
 * the physical count and the difference is saved as an adjustment (INVUI-504).
 *
 * GET  /inventory/history/:patientId/:pharmacyOrderId
 *   data: [{ id, type, qty_change, balance_after, reason, source_type,
 *            source_id, created_by, created, created_by_username }]
 * POST /inventory/adjust/:patientId
 *   { pharmacy_order_id, qty_change, reason }
 *   qty_change is signed and never 0; the reason is required. An adjustment
 *   is a new history row — earlier rows are never edited.
 */

const authHeaders = () => {
  const tokenStr = localStorage.getItem("accessToken");
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl ? `${baseUrl}${path}` : path;
};

const toNumber = (v) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v);

const formatQty = (n) => {
  if (n === null) return "—";
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
};

// "PURCHASE_RETURN" -> "Purchase return"
const humanize = (s) => {
  const text = String(s || "").replace(/_/g, " ").trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

// Type column: a solid coloured icon next to a full-size label.
const TYPE_STYLE = {
  RECEIPT: { label: "Received", icon: faTruck, bg: "success" },
  DISPENSE: { label: "Dispensed", icon: faPills, bg: "info" },
  ADJUST: { label: "Adjusted", icon: faSlidersH, bg: "warning" },
  ADJUSTMENT: { label: "Adjusted", icon: faSlidersH, bg: "warning" }
};

const typeStyle = (type) =>
  TYPE_STYLE[type] || { label: humanize(type) || "—", icon: faExchangeAlt, bg: "secondary" };

const SOURCE_LABEL = {
  medpass: "Med pass",
  confirmation: "Delivery count"
};

export const getStockHistory = async (patientId, pharmacyOrderId) => {
  const res = await axios.get(
    apiUrl(
      `/inventory/history/${encodeURIComponent(patientId)}/${encodeURIComponent(pharmacyOrderId)}`
    ),
    { headers: authHeaders() }
  );
  const list = Array.isArray(res.data?.data) ? res.data.data : [];
  return list
    .map((r, i) => ({
      key: r.id ?? `h-${i}`,
      id: toNumber(r.id),
      type: String(r.type || "").toUpperCase(),
      change: toNumber(r.qty_change),
      balance: toNumber(r.balance_after),
      reason: r.reason || "",
      sourceType: r.source_type || "",
      sourceId: r.source_id ?? null,
      by: r.created_by_username || (r.created_by ? `User ${r.created_by}` : ""),
      at: r.created || null
    }))
    .sort((a, b) => moment(b.at).valueOf() - moment(a.at).valueOf() || (b.id || 0) - (a.id || 0));
};

export const adjustStock = async (patientId, body) => {
  const res = await axios.post(apiUrl(`/inventory/adjust/${encodeURIComponent(patientId)}`), body, {
    headers: authHeaders()
  });
  return res.data;
};

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

/* ------------------------------------------------------------------ */
/* Current balance                                                     */
/* ------------------------------------------------------------------ */

const BalanceCard = ({ balance, latestAt, entryCount }) => {
  const updated = latestAt ? moment(latestAt) : null;
  return (
    <div className="rounded-3 bg-body p-3">
      <div className="d-flex align-items-center gap-2 fw-semibold text-900 fs-9">
        Current Balance
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="current-balance-help">
              What the system has on record: deliveries received, minus doses given, plus or minus
              adjustments.
            </Tooltip>
          }
        >
          <span className="text-600" style={{ cursor: "help" }} aria-label="About the current balance">
            <FontAwesomeIcon icon={faQuestionCircle} />
          </span>
        </OverlayTrigger>
      </div>
      <div className="fw-bold text-900 fs-4 lh-1 mt-2">{formatQty(balance)}</div>
      <div className="text-600 fs-10 mt-3">
        Last updated: {updated && updated.isValid() ? updated.format("MM/DD/YY h:mm A") : "—"}
      </div>
    </div>
  );
};

BalanceCard.propTypes = {
  balance: PropTypes.number.isRequired,
  latestAt: PropTypes.string,
  entryCount: PropTypes.number.isRequired
};

/* ------------------------------------------------------------------ */
/* Inventory audit — enter the physical count, the difference becomes */
/* an adjustment (POST /inventory/adjust/:patientId)                   */
/* ------------------------------------------------------------------ */

const InventoryAuditPanel = ({ patientId, line, systemBalance, onSaved }) => {
  const [count, setCount] = useState("");
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const counted = Number(count);
  const hasCount = count !== "" && Number.isFinite(counted);
  // Physical count minus what the system has; this is the qty_change sent.
  const adjustment =
    hasCount && counted >= 0 ? Number((counted - systemBalance).toFixed(2)) : null;

  const errors = {};
  if (!hasCount) errors.count = "Enter the physical count.";
  else if (counted < 0) errors.count = "The count can't be negative.";
  if (!reason.trim()) errors.reason = "A reason is required.";
  const valid = Object.keys(errors).length === 0 && adjustment !== 0;

  const reset = () => {
    setCount("");
    setReason("");
    setTried(false);
    setSaveError("");
  };

  const save = async () => {
    setTried(true);
    if (!valid || saving || !line) return;
    setSaving(true);
    setSaveError("");
    try {
      const res = await adjustStock(patientId, {
        pharmacy_order_id: String(line.pharmacy_order_id),
        qty_change: adjustment,
        reason: reason.trim()
      });
      toast.success(res?.message || "Audit saved.");
      setSaving(false);
      reset();
      onSaved();
    } catch (err) {
      setSaveError(errorText(err, "The audit could not be saved."));
      setSaving(false);
    }
  };

  const adjustmentTone =
    adjustment === null || adjustment === 0
      ? "bg-body-emphasis text-500"
      : adjustment < 0
      ? "bg-danger-subtle text-danger"
      : "bg-body-emphasis text-success";

  const note = !hasCount || counted < 0
    ? "Enter the physical count to see how the balance will change."
    : adjustment === 0
    ? "The physical count matches the current balance, so there is nothing to adjust."
    : null;

  return (
    <div>
      <div className="fw-bold text-900 fs-8">Inventory Adjustment</div>
      <div className="text-600 fs-10 mb-3">
        Enter the actual physical count and we&apos;ll calculate the adjustment.
      </div>

      {/* The table-header green, lightened, on a white base. */}
      <div className="rounded-3 bg-body-emphasis">
        <div className="rounded-3 border bg-200 bg-opacity-10 px-3 py-2">
          <Row className="g-0">
            <Col xs={12} sm className="py-2 pe-sm-3">
              <div className="fw-semibold text-800 fs-10 mb-2">Current Balance</div>
              <div className="fw-bold text-900 fs-6 lh-1 py-1">{formatQty(systemBalance)}</div>
            </Col>
            <Col xs="auto" className="d-none d-sm-flex py-2">
              <div className="vr text-300" />
            </Col>
            <Col xs={12} sm className="py-2 px-sm-3">
              <div className="fw-semibold text-800 fs-10 mb-2">Physical Count</div>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="number"
                  min={0}
                  step="any"
                  aria-label="Physical count"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  isInvalid={tried && Boolean(errors.count)}
                  style={{ width: 110, backgroundImage: "none" }}
                />
              </div>
            </Col>
            <Col xs="auto" className="d-none d-sm-flex py-2">
              <div className="vr text-300" />
            </Col>
            <Col xs={12} sm className="py-2 ps-sm-3">
              <div className="fw-semibold text-800 fs-10 mb-2">Adjustment</div>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`d-inline-flex align-items-center justify-content-center rounded-2 fw-bold fs-7 px-3 ${adjustmentTone}`}
                  style={{ minWidth: 64, height: 38 }}
                  aria-label="Adjustment"
                >
                  {adjustment === null ? "—" : `${adjustment > 0 ? "+" : ""}${formatQty(adjustment)}`}
                </span>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      {tried && errors.count && <div className="invalid-feedback d-block">{errors.count}</div>}

      <Form.Label htmlFor="audit-reason" className="fw-semibold text-900 fs-10 mt-3 mb-1">
        Reason <span className="text-500 fw-normal">(required)</span>
      </Form.Label>
      <Form.Control
        as="textarea"
        rows={2}
        id="audit-reason"
        className="fs-10"
        placeholder="e.g. 2 tablets missing during inventory count"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        isInvalid={tried && Boolean(errors.reason)}
      />
      {tried && errors.reason && <div className="invalid-feedback d-block">{errors.reason}</div>}

      <div className="d-flex align-items-center gap-2 rounded-3 bg-body px-3 py-2 mt-3 fs-10 text-700">
        <FontAwesomeIcon icon={faInfoCircle} className="text-600" />
        {note ? (
          <span>{note}</span>
        ) : (
          <span>
            This will change the inventory balance from{" "}
            <span className="fw-bold text-900">
              {formatQty(systemBalance)} → {formatQty(systemBalance + adjustment)}
            </span>
            .
          </span>
        )}
      </div>

      {saveError && (
        <Alert variant="danger" className="fs-10 mt-3 mb-0 py-2">
          <span className="fw-bold">Nothing was saved.</span> {saveError}
        </Alert>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="falcon-default" onClick={reset} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || adjustment === 0}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Adjustment"
          )}
        </Button>
      </div>
    </div>
  );
};

InventoryAuditPanel.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // Briefly null while the modal fades out after closing.
  line: PropTypes.shape({
    pharmacy_order_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  systemBalance: PropTypes.number.isRequired,
  onSaved: PropTypes.func.isRequired
};

const StockHistoryModal = ({ patientId, line, labelOf, onHide, onAdjusted }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!line || !line.pharmacy_order_id) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    setEntries([]);
    try {
      const list = await getStockHistory(patientId, line.pharmacy_order_id);
      if (requestId === requestRef.current) setEntries(list);
    } catch (err) {
      if (requestId === requestRef.current) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "The stock history could not be loaded."
        );
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [patientId, line]);

  useEffect(() => {
    load();
  }, [load]);

  // After an audit is saved: reload the history and let the list refresh.
  const afterAdjust = () => {
    load();
    if (onAdjusted) onAdjusted();
  };

  const hasReason = entries.some((e) => e.reason);
  const latest = entries[0];
  // With no history yet there is nothing on record, so the balance is 0.
  const systemBalance = latest && latest.balance !== null ? latest.balance : 0;

  return (
    <Modal show={Boolean(line)} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-bottom border-200">
        {line && (
          <Modal.Title as="div" className="me-3">
            <div className="fw-bold text-900 fs-9">
              <FontAwesomeIcon icon={faHistory} className="text-primary me-2" />
              {line.drug_name}  {line.schedule && (
                <ScheduleBadge token={line.schedule} title={labelOf ? labelOf(line.schedule) : undefined} />
              )}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1 text-600 fs-10 fw-normal">
              {/* {[line.strength, line.pharmacy_order_id ? `Rx ${line.pharmacy_order_id}` : ""]
                .filter(Boolean)
                .join(" · ")} */}
              
            </div>
          </Modal.Title>
        )}
      </Modal.Header>

      <Modal.Body className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status" />
            <p className="mt-3 text-600 fs-10 mb-0">Loading stock history...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="d-flex align-items-start border-0 rounded-0 fs-10 mb-0">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
            <div className="flex-1">
              <div className="fw-bold">We couldn&apos;t load the stock history</div>
              <div>{error}</div>
            </div>
            <Button variant="outline-danger" size="sm" className="ms-3 rounded-pill px-3" onClick={load}>
              Retry
            </Button>
          </Alert>
        ) : (
          <>
            <div className="p-3 bg-body-tertiary border-bottom border-200">
              <Row className="g-3">
                {/* <Col md={4}>
                  <BalanceCard
                    balance={systemBalance}
                    latestAt={latest ? latest.at : null}
                    entryCount={entries.length}
                  />
                </Col> */}
                <Col xs="auto" className="d-none d-md-flex">
                  <div className="vr text-200" />
                </Col>
                <Col md>
                  <InventoryAuditPanel
                    key={line ? line.pharmacy_order_id : "none"}
                    patientId={patientId}
                    line={line}
                    systemBalance={systemBalance}
                    onSaved={afterAdjust}
                  />
                </Col>
              </Row>
            </div>

            {entries.length === 0 ? (
              <div className="text-center py-5 px-3">
                <FontAwesomeIcon icon={faHistory} className="text-400 fs-5 mb-3" />
                <h6 className="text-900 fw-bold mb-1 fs-9">No stock history yet</h6>
                <p className="text-600 fs-10 mb-0">
                  Deliveries received, doses given and adjustments for this medication will appear
                  here.
                </p>
              </div>
            ) : (
              <Table responsive hover size="sm" className="mb-0 fs-10 align-middle">
                <thead>
                  <tr className="bg-200 text-900">
                    <th className="ps-3 fs-11 text-uppercase fw-semibold text-start">Date</th>
                    <th className="fs-11 text-uppercase fw-semibold text-start">Type</th>
                    <th className="fs-11 text-uppercase fw-semibold text-center">Change</th>
                    <th className="fs-11 text-uppercase fw-semibold text-center">Balance</th>
                    <th className="fs-11 text-uppercase fw-semibold text-start">Source</th>
                    <th className={`fs-11 text-uppercase fw-semibold text-start ${hasReason ? "" : "pe-3"}`}>
                      By
                    </th>
                    {hasReason && (
                      <th className="pe-3 fs-11 text-uppercase fw-semibold text-start">Reason</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const style = typeStyle(e.type);
                    const at = e.at ? moment(e.at) : null;
                    return (
                      <tr key={e.key}>
                        <td className="ps-3 text-start text-nowrap">
                          {at && at.isValid() ? (
                            <>
                              <div className="text-900">{at.format("MM/DD/YY")}</div>
                              <div className="text-600 fs-11">{at.format("h:mm A")}</div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="text-start">
                          <span className="d-inline-flex align-items-center gap-2 fw-semibold text-900 text-nowrap">
                            <span
                              className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 text-white bg-${style.bg}`}
                              style={{ width: 24, height: 24 }}
                            >
                              <FontAwesomeIcon icon={style.icon} style={{ fontSize: 11 }} />
                            </span>
                            {style.label}
                          </span>
                        </td>
                        <td
                          className={`text-center fw-semibold ${
                            e.change > 0 ? "text-success" : e.change < 0 ? "text-danger" : "text-600"
                          }`}
                        >
                          {e.change > 0 ? "+" : ""}
                          {formatQty(e.change)}
                        </td>
                        <td className="text-center fw-bold text-900">{formatQty(e.balance)}</td>
                        <td className="text-start">
                          <div className="text-900">
                            {SOURCE_LABEL[e.sourceType] || humanize(e.sourceType) || "—"}
                          </div>
                          {/* {e.sourceId !== null && e.sourceId !== "" && (
                            <div className="text-500 fs-11">#{e.sourceId}</div>
                          )} */}
                        </td>
                        <td className={`text-start text-700 ${hasReason ? "" : "pe-3"}`}>{e.by || "—"}</td>
                        {hasReason && <td className="pe-3 text-start text-700">{e.reason || "—"}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top border-200">
        <Button variant="falcon-default" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

StockHistoryModal.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  line: PropTypes.shape({
    drug_name: PropTypes.string,
    strength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pharmacy_order_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    schedule: PropTypes.string
  }),
  labelOf: PropTypes.func,
  onHide: PropTypes.func.isRequired,
  // Called after a successful adjustment so the medication list can refresh.
  onAdjusted: PropTypes.func
};

export default StockHistoryModal;
